// Наружный поток событий events() — мультиплексор для client-store. SPEC client-transport §4.8.
//
// EventHub раздаёт TransportEvent всем активным async-итераторам, открытым через events(). Реализует
// backpressure-нейтральную модель «push в очередь / await на pull», как в контракт-фейке стора
// (client-store/test/fake-transport.ts) — чтобы публичный Transport структурно подошёл под
// client-store TransportPort без правок. Дедуп входящих server-событий (§4.9) — в DedupGate ниже.
/**
 * Хаб событий: `emit` рассылает событие во всех подписчиков; `events()` открывает независимый
 * async-iterable. Поддерживает несколько одновременных потребителей (каждый видит все события с
 * момента подписки). `close()` завершает все потоки.
 */
export class EventHub {
    subscribers = new Set();
    closed = false;
    emit(event) {
        if (this.closed)
            return;
        for (const sub of this.subscribers) {
            const r = sub.resolvers.shift();
            if (r)
                r({ value: event, done: false });
            else
                sub.queue.push(event);
        }
    }
    events() {
        const hub = this;
        const sub = { queue: [], resolvers: [], done: false };
        hub.subscribers.add(sub);
        return {
            [Symbol.asyncIterator]() {
                return {
                    next() {
                        const queued = sub.queue.shift();
                        if (queued)
                            return Promise.resolve({ value: queued, done: false });
                        if (sub.done || hub.closed) {
                            hub.subscribers.delete(sub);
                            return Promise.resolve({ value: undefined, done: true });
                        }
                        return new Promise((resolve) => sub.resolvers.push(resolve));
                    },
                    return() {
                        sub.done = true;
                        hub.subscribers.delete(sub);
                        for (const r of sub.resolvers.splice(0))
                            r({ value: undefined, done: true });
                        return Promise.resolve({ value: undefined, done: true });
                    },
                };
            },
        };
    }
    close() {
        this.closed = true;
        for (const sub of this.subscribers) {
            sub.done = true;
            for (const r of sub.resolvers.splice(0))
                r({ value: undefined, done: true });
        }
        this.subscribers.clear();
    }
}
/**
 * Лёгкий LRU-дедуп входящих server-событий (§4.9): WS может прислать одно и то же
 * `message.new`/`sync.delta`-сообщение дважды (flapping, перекрытие live-хвоста и sync-добора).
 * Дедупим message.new по `(chatId, seq)`, edited/deleted/reaction — по `messageId` (последняя
 * правка вытесняет). `ack`/`receipt`/presence/typing/ready/error НЕ дедупим (идемпотентны/эфемерны).
 * Семантическую сверку всё равно делает стор; гейт лишь гасит явные дубли.
 */
export class DedupGate {
    seen;
    order;
    cap;
    constructor(capacity = 2048) {
        this.cap = capacity;
        this.seen = new Set();
        this.order = [];
    }
    /** true — событие новое (пробрасываем наружу); false — дубль (гасим). */
    admit(event) {
        const key = this.keyOf(event);
        if (key === null)
            return true; // событие не подлежит дедупу
        if (this.seen.has(key))
            return false;
        this.remember(key);
        return true;
    }
    /** Сбрасывает память дедупа (например, при полном переподключении, если нужно). */
    reset() {
        this.seen.clear();
        this.order.length = 0;
    }
    remember(key) {
        this.seen.add(key);
        this.order.push(key);
        while (this.order.length > this.cap) {
            const evicted = this.order.shift();
            if (evicted !== undefined)
                this.seen.delete(evicted);
        }
    }
    /** Ключ дедупа или null, если событие не дедупится. */
    keyOf(event) {
        switch (event.t) {
            case 'message.new':
                return msgKey(event.chatId, event.message.seq);
            case 'message.edited':
                return `edited:${event.messageId}:${event.editedAt}`;
            case 'message.deleted':
                return `deleted:${event.messageId}:${event.deletedAt}`;
            case 'reaction.set':
                return `reaction:${event.messageId}:${event.userId}:${event.emoji}:${event.set}`;
            case 'sync.delta':
                // sync.delta как пакет не дедупим целиком (upToSeq двигается); дедуп его сообщений
                // делает применение message-в-стор по seq. Но чтобы не задваивать ИДЕНТИЧНЫЙ пакет
                // (повтор при перекрытии), ключуем по (chatId, upToSeq, кол-во).
                return `syncdelta:${event.chatId}:${event.upToSeq}:${event.messages.length}`;
            default:
                return null;
        }
    }
}
function msgKey(chatId, seq) {
    return `msgnew:${chatId}:${seq}`;
}
//# sourceMappingURL=event-hub.js.map